import { useEffect, useMemo, useRef } from "react";
import {
  getCustomTagSearchKey,
  formatCustomTagLabel,
  WorkflowType,
} from "@/pages/query/types";
import {
  useDatatableContext,
  FilterConfig,
} from "@/components/tables/contexts/DatatableContextProvider";
import { removeFilter } from "@/components/tables/contexts/datatableSlice";
import { FilterType } from "@/components/tables/types";
import { getSnowflakeJobsFilterCustomTagValues } from "@/helpers/apis";
import {
  getDateByEndOfDayWithoutTimeoffset,
  getDateByStartOfDayWithoutTimeoffset,
} from "@/components/dateRange/utils";
import { stringPageExtractor } from "@/pages/query/utils/snowflakeJobsFilterConfig";

const PAGE_SIZE = 50;

/**
 * Build a Record<tag, selectedValues[]> from the configured custom tag keys
 * by reading currently-selected values via the provided getter.
 *
 * Tag keys come from the bulk filters response (capped at 15). Tag *values*
 * are fetched on-demand by the per-tag API_DROPDOWN registered in
 * {@link useRegisterCustomTagFilters}; this hook only reads selected values
 * to feed downstream queries (e.g. graph_data's custom_tag_filters).
 */
export const useCustomTagFilters = (
  customTagKeys: string[] | undefined,
  getFilterValue: <T = string | string[] | number | Date>(key: string) => T | undefined
): Record<string, string[]> => {
  return useMemo(() => {
    const result: Record<string, string[]> = {};
    if (customTagKeys) {
      for (const tag of customTagKeys) {
        const values = getFilterValue<string[]>(getCustomTagSearchKey(tag)) || [];
        if (values.length > 0) {
          result[tag] = values;
        }
      }
    }
    return result;
  }, [customTagKeys, getFilterValue]);
};

/**
 * Register one API_DROPDOWN filter per configured custom tag with the
 * DatatableContext so they appear in the "More Filters" sheet. Each
 * dropdown lazily hits /custom_tags/{tag_key} with search/page params.
 *
 * Date inputs feed into the apiFunction through refs, so changing the
 * date range doesn't cause a re-register (which would overwrite each
 * filter's selected value via the setFilters reducer's merge semantics).
 */
export const useRegisterCustomTagFilters = (
  customTagKeys: string[] | undefined,
  workflowType: WorkflowType,
  startDate: Date,
  endDate: Date
): void => {
  const { registerFilters, dispatch } = useDatatableContext();

  // Mutate refs during render so the apiFunction below always sees the
  // latest dates without participating in any dep array.
  const startDateRef = useRef(startDate);
  const endDateRef = useRef(endDate);
  startDateRef.current = startDate;
  endDateRef.current = endDate;

  const filterConfigs = useMemo(() => {
    if (!customTagKeys || customTagKeys.length === 0) return null;
    const configs: Record<string, FilterConfig> = {};
    for (const tag of customTagKeys) {
      const searchKey = getCustomTagSearchKey(tag);
      configs[searchKey] = {
        label: formatCustomTagLabel(tag),
        searchKey,
        isArray: true,
        filterType: FilterType.API_DROPDOWN,
        seeMore: true,
        pageSize: PAGE_SIZE,
        apiFunction: ({ prefix, page, size }: { prefix?: string; page?: number; size?: number }) =>
          getSnowflakeJobsFilterCustomTagValues(tag, {
            start_date: getDateByStartOfDayWithoutTimeoffset(startDateRef.current).toISOString(),
            end_date: getDateByEndOfDayWithoutTimeoffset(endDateRef.current)?.toISOString(),
            workflow_type: workflowType,
            search: prefix || undefined,
            page,
            size: size ?? PAGE_SIZE,
          }),
        extractorFunction: stringPageExtractor,
      };
    }
    return configs;
    // Dates intentionally omitted — read via refs inside apiFunction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customTagKeys, workflowType]);

  // Track previously-registered search keys so we can unregister ones
  // dropped from customTagKeys (DatatableContext.registerFilters merges
  // rather than replaces — without an explicit removeFilter the stale
  // config would keep appearing in the More Filters sheet).
  const registeredKeysRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    const next = new Set(Object.keys(filterConfigs ?? {}));
    const prev = registeredKeysRef.current;

    for (const key of prev) {
      if (!next.has(key)) dispatch(removeFilter({ columnId: key }));
    }
    if (filterConfigs) registerFilters(filterConfigs);

    registeredKeysRef.current = next;
  }, [filterConfigs, registerFilters, dispatch]);
};
